/* @jsx h */
import { defineComponent, h, ref, computed } from "vue";
import {
  Promotion,
  PromotionReward,
  PromotionRequirement,
  RewardDays,
} from "@lemmonhq/types";
import { RightSheet } from "@lemmonhq/uikit";
import { PromotionFormData } from "./types";
import {
  // Requirement editors
  CoinRequirementEditor,
  ProductRequirementEditor,
  ProductCategoryRequirementEditor,
  ContextRequirementEditor,
  ServiceMethodRequirementEditor,
  // Days editor
  DaysEditor,
} from "./components";
import { PromotionRewardEditor } from "../../components/PromotionRewardEditor";
import { api } from "@app/data/api";

export const NewPromotionEditor = defineComponent<{
  promotion?: Promotion;
  onClose: () => void;
  onSave: (promotion: Promotion) => void;
}>({
  name: "NewPromotionEditor",
  props: ["promotion", "onClose", "onSave"],
  setup(props) {
    const saving = ref(false);

    const formData = ref<PromotionFormData>({
      name: props.promotion?.name || "",
      requirements: props.promotion?.requirements || [
        {
          type: "product",
          product_ids: [],
          quantity: 1,
        },
      ],
      reward: props.promotion?.reward || {
        type: "product_discount",
        mode: "percentage",
        value: 10,
      },
      days: props.promotion?.days || {},
      enabled: props.promotion?.enabled ?? true,
    });

    const addRequirement = () => {
      // coin_program_id is empty until the picker sets it; an unselected coin
      // requirement fails closed at validation.
      formData.value.requirements.push({
        type: "coin",
        coin_program_id: "",
        coin_type: "",
        amount: 10,
      });
    };

    const removeRequirement = (index: number) => {
      formData.value.requirements.splice(index, 1);
    };

    const updateRequirement = (
      index: number,
      requirement: PromotionRequirement,
    ) => {
      formData.value.requirements[index] = requirement;
    };

    const updateReward = (reward: PromotionReward) => {
      formData.value.reward = reward;
    };

    const updateDays = (days: RewardDays) => {
      formData.value.days = days;
    };

    const changeRequirementType = (
      index: number,
      newType: PromotionRequirement["type"],
    ) => {
      let newRequirement: PromotionRequirement;

      switch (newType) {
        case "coin":
          newRequirement = {
            type: "coin",
            coin_program_id: "",
            coin_type: "",
            amount: 10,
          };
          break;
        case "product":
          newRequirement = { type: "product", product_ids: [], quantity: 1 };
          break;
        case "product_category":
          newRequirement = {
            type: "product_category",
            category_ids: [],
            quantity: 1,
          };
          break;
        case "context":
          newRequirement = {
            type: "context",
            key: "firstTimeCustomer",
            value: true,
          };
          break;
        case "service_method":
          newRequirement = { type: "service_method", value: "takeaway" };
          break;
        default:
          newRequirement = {
            type: "coin",
            coin_program_id: "",
            coin_type: "",
            amount: 10,
          };
      }

      updateRequirement(index, newRequirement);
    };


    const renderRequirementEditor = (
      requirement: PromotionRequirement,
      index: number,
    ) => {
      switch (requirement.type) {
        case "coin":
          return (
            <CoinRequirementEditor
              {...{
                value: requirement,
                onChange: (req: PromotionRequirement) =>
                  updateRequirement(index, req),
              }}
            />
          );
        case "product":
          return (
            <ProductRequirementEditor
              {...{
                value: requirement,
                onChange: (req: PromotionRequirement) =>
                  updateRequirement(index, req),
              }}
            />
          );
        case "product_category":
          return (
            <ProductCategoryRequirementEditor
              {...{
                value: requirement,
                onChange: (req: PromotionRequirement) =>
                  updateRequirement(index, req),
              }}
            />
          );
        case "context":
          return (
            <ContextRequirementEditor
              {...{
                value: requirement,
                onChange: (req: PromotionRequirement) =>
                  updateRequirement(index, req),
              }}
            />
          );
        case "service_method":
          return (
            <ServiceMethodRequirementEditor
              {...{
                value: requirement,
                onChange: (req: PromotionRequirement) =>
                  updateRequirement(index, req),
              }}
            />
          );
        default:
          return null;
      }
    };

    const handleSubmit = async () => {
      try {
        saving.value = true;

        const promotionData = {
          name: formData.value.name,
          shop_id: 1, // TODO: Get from context
          requirements: formData.value.requirements,
          reward: formData.value.reward,
          days: formData.value.days,
          enabled: formData.value.enabled,
        };

        let result: Promotion;
        if (props.promotion) {
          result = await api.updatePromotion({
            ...promotionData,
            id: props.promotion.id,
          });
        } else {
          result = await api.createPromotion(promotionData);
        }

        props.onSave(result);
        props.onClose();
      } catch (error: any) {
        console.error("Error saving promotion:", error);
        alert("Failed to save promotion: " + error.message);
      } finally {
        saving.value = false;
      }
    };

    const isFormValid = computed(() => {
      return formData.value.name.trim().length > 0;
    });

    return () => {
      const modalProps = {
        title: (
          <div class="flex flex-1 items-center text-1xl text-accent gap-1 text-2xl">
            <span class="material-symbols-outlined">local_offer</span>
            {props.promotion
              ? `Edit ${props.promotion.name}`
              : "Create Promotion"}
          </div>
        ),
        onClose: props.onClose,
        isOpen: true,
      };

      return (
        <RightSheet {...modalProps}>
          <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div class="min-h-0 flex-1 space-y-4 overflow-y-auto p-2">
              {/* Basic Info */}
              <div class="card  ">
                <div class="card-body p-0">
                  <div class="form-control">
                    <label class="label">
                      <span class="label-text text-accent font-medium">
                        Promotion Name *
                      </span>
                    </label>
                    <input
                      type="text"
                      value={formData.value.name}
                      onInput={(e) =>
                        (formData.value.name = (
                          e.target as HTMLInputElement
                        ).value)
                      }
                      class="input input-bordered input-md focus:input-primary"
                      placeholder="e.g., Buy 2 Get 1 Free Pizza"
                    />
                    <label class="label">
                      <span class="label-text-alt">
                        Give your promotion a clear, descriptive name
                      </span>
                    </label>
                  </div>

                  <div class="form-control">
                    <label class="label cursor-pointer justify-start gap-3">
                      <input
                        type="checkbox"
                        checked={formData.value.enabled}
                        onChange={(e) =>
                          (formData.value.enabled = (
                            e.target as HTMLInputElement
                          ).checked)
                        }
                        class="checkbox checkbox-primary"
                      />
                      <span class="text-accent label-text font-medium">
                        Enable this promotion
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Requirements Section */}
              <div class="card ">
                <div class="card-body p-0">
                  <div class="flex items-center justify-between mb-4">
                    <h3 class="card-title text-accent text-xl text-accent">
                      Requirements
                    </h3>
                    <button
                      type="button"
                      onClick={addRequirement}
                      class="btn btn-sm btn-primary"
                    >
                      <span class="text-lg mr-1">+</span>
                      Add Requirement
                    </button>
                  </div>

                  {formData.value.requirements.length > 1 && (
                    <div class="alert  mb-4">
                      <div class="flex items-center gap-2">
                        <div class="text-info text-xl">ℹ️</div>
                        <div>
                          <div class="text-accent">Multiple Requirements</div>
                          <div class="text-sm">
                            All requirements must be met for the promotion to
                            trigger
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div class="space-y-4">
                    {formData.value.requirements.map((requirement, index) => (
                      <div key={index} class="relative">
                        <div class="flex items-center gap-2 mb-3">
                          <select
                            value={requirement.type}
                            onChange={(e) =>
                              changeRequirementType(
                                index,
                                (e.target as HTMLSelectElement)
                                  .value as PromotionRequirement["type"],
                              )
                            }
                            class="select select-bordered focus:select-primary flex-1"
                          >
                            {false && (
                              <>
                                <option value="coin hidden">
                                  💰 Coin/Points
                                </option>
                                <option value="context hidden">
                                  👤 Customer Context
                                </option>
                              </>
                            )}{" "}
                            <option value="product">
                              🍕 Specific Products
                            </option>
                            <option value="product_category">
                              📂 Product Categories
                            </option>
                            <option value="service_method">
                              🚚 Service Method
                            </option>
                          </select>

                          <button
                            type="button"
                            onClick={() => removeRequirement(index)}
                            class="btn btn-sm btn-ghost text-error hover:bg-error/10 hover:text-error"
                            title="Delete requirement"
                          >
                            <span class="material-symbols-outlined text-lg">
                              delete
                            </span>
                          </button>
                        </div>

                        {renderRequirementEditor(requirement, index)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Reward Section */}
              <PromotionRewardEditor
                value={formData.value.reward}
                onChange={updateReward}
                requirements={formData.value.requirements}
                title="Reward"
              />

              {/* Days & Times Section */}
              <DaysEditor value={formData.value.days} onChange={updateDays} />
            </div>
            <div class="sticky bottom-0 shrink-0 border-t  bg-base-100 p-4">
              <button
                onClick={handleSubmit}
                class="btn btn-primary btn-block"
                disabled={!isFormValid.value || saving.value}
              >
                {saving.value ? (
                  <span class="loading loading-spinner loading-sm mr-2"></span>
                ) : null}
                {props.promotion ? "Save" : "Create"}
              </button>
            </div>
          </div>
        </RightSheet>
      );
    };
  },
});
